import React, { useEffect } from 'react';
import { HiChevronDown, HiChevronRight } from 'react-icons/hi';
import PortfolioComposite from '../../../../api/models/portfolioComposite';
import PortfolioPosition from '../../../../api/models/portfolioPosition';
import CollapsiblePosition from './collapsible-position';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { setCollapsedState, selectIsCategoryCollapsed, setSelectedComposite } from './collapsible-composite-slice';
import ValueDisplayFlipCard from './value-display-flip-card';
import DeviationGauge from '../../deviation-gauge';
import { instanceToPlain } from 'class-transformer';

interface AssetAllocationCategoryProps {
  portfolioComposite: PortfolioComposite
  level: number
  deviationLimit: number
  isLastComponent: boolean
}

/**
 * A button that flips on click and alternates displayed portfolio values between MONEY (R$) and PERCENTAGE (%).
 */
const CollapsibleComposite: React.FC<AssetAllocationCategoryProps> = (props: AssetAllocationCategoryProps) => {

  // Get the 'dispatch' method from the store
  const dispatch = useAppDispatch();
  const isRootCategory = !props.portfolioComposite?.parentCompositeId;
  const itselfCollapsed = useAppSelector(selectIsCategoryCollapsed(props.portfolioComposite.id || ''));
  const parentCollapsed = useAppSelector(selectIsCategoryCollapsed(props.portfolioComposite.parentCompositeId || ''));
  const hasChildren = props.portfolioComposite.portfolioComponents.length > 0;

  // Can only be expanded if parent is also expanded.
  useEffect(() => {
    if (!isRootCategory && props.portfolioComposite?.id && !itselfCollapsed && parentCollapsed) {
      dispatch(setCollapsedState({
        id: props.portfolioComposite.id,
        collapsed: true
      }));
    }
  });

  // Always exanded if it is the root category.
  useEffect(() => {
    if (isRootCategory && props.portfolioComposite.id) {
      dispatch(setCollapsedState({
        id: props.portfolioComposite.id,
        collapsed: false
      }));
    }
  }, [dispatch, isRootCategory, props.portfolioComposite.id]);

  const selectCompositeHandler = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    dispatch(setSelectedComposite(Object(instanceToPlain(props.portfolioComposite))));
    if (!isRootCategory) {
      // Collapse
      dispatch(setCollapsedState({
        id: props.portfolioComposite.id ?? '',
        collapsed: !itselfCollapsed
      }));
    }
  }

  const unselectCategoryHandler = (e: React.FocusEvent<HTMLTableRowElement>) => {
    dispatch(setSelectedComposite(undefined));
  }

  const bgColor = () => {
    switch (props.level) {
      case 1:
        return 'bg-black/0 dark:bg-black/0 border-b border-blue-300/40'
      case 2:
        return 'bg-black/0 dark:bg-black/0 border-b border-blue-300/40'
      case 3:
        return 'bg-black/25 dark:bg-black/25 border-b border-blue-300/30'
      default:
        return 'bg-black/50 dark:bg-black/50 border-b border-blue-300/20'
    }
  }

  const bgColorGapRow = () => {
    switch (props.level) {
      case 1:
        return ''
      case 2:
        return 'bg-gradient-to-b from-black/25 from-10% to-black/0'
      case 3:
        return 'bg-gradient-to-b from-black/50 from-10% to-black/25'
      default:
        return 'bg-gradient-to-b from-black/50 from-10% to-black/50'
    }
  }

  const isMobile = window.outerWidth < 1024;

  let own;
  own = (
    <tr onBlur={unselectCategoryHandler} onClick={selectCompositeHandler} tabIndex={0} hidden={!isRootCategory && parentCollapsed} className={`${bgColor()} relative group cursor-pointer focus-within:bg-orange-400/30 hover:bg-orange-400/30`}>
      <td className='cursor-pointer'>
        <div hidden={isRootCategory || !hasChildren}>
          <div className='flex items-center justify-start'>
            {props.level > 1 && [...Array(props.level - 1)].map(x => (<div key={Math.random()} className='text-white/0 w-2 lg:w-4'>.</div>))}
            {itselfCollapsed ?
              <HiChevronDown size={isMobile ? 14 : 18} /> :
              <HiChevronRight size={isMobile ? 14 : 18} />
            }
          </div>
        </div>
      </td>
      <td colSpan={2} className='text-left pl-2 pr-6 py-3'>
        <div className={`flex ${isRootCategory ? 'uppercase' : ''}`}>{props.portfolioComposite.category?.name}</div>
      </td>
      <td className='text-center py-3 z-0'>
        <ValueDisplayFlipCard
          moneyValue={props.portfolioComposite.idealNotional()}
          percentageValue={props.portfolioComposite.idealPercentage() * 100}
          className='cursor-pointer text-center w-full' />
      </td>
      <td className='max-lg:hidden flex px-6 py-3 justify-center'>
        <DeviationGauge
          deviation={props.portfolioComposite.deviation()}
          deviationLimit={props.deviationLimit}
          className='w-14'
          valueClassName='cursor-pointer text-center w-14 text-xs font-semibold bg-transparent'
        />
      </td>
      <td className='max-lg:hidden text-center py-3'>
        <ValueDisplayFlipCard
          moneyValue={props.portfolioComposite.notional()}
          percentageValue={props.portfolioComposite.currentPercentage() * 100}
          className='cursor-pointer text-center w-full' />
      </td>
      <td className='text-center px-6 py-3'>
        <label className='cursor-pointer'>{isRootCategory ? '' : props.portfolioComposite.weight}</label>
      </td>
    </tr >
  );

  let children;
  if (props.portfolioComposite.portfolioComponents && props.portfolioComposite.portfolioComponents.length > 0) {
    children = props.portfolioComposite.portfolioComponents.map((child, index) => {
      if (child instanceof PortfolioComposite) {
        return (
          <CollapsibleComposite
            key={child.id}
            deviationLimit={props.deviationLimit}
            level={props.level + 1}
            portfolioComposite={child}
            isLastComponent={props.portfolioComposite.portfolioComponents.indexOf(child) === props.portfolioComposite.portfolioComponents.length - 1}
          />
        )
      } else if (child instanceof PortfolioPosition) {
        return (
          <CollapsiblePosition
            key={child.id}
            deviationLimit={props.deviationLimit}
            portfolioPosition={child}
          />
        )
      } else {
        return null;
      }
    });
  }

  let gapRow = (props.isLastComponent || isRootCategory || itselfCollapsed) ? <></> : <tr className={`${bgColorGapRow()} h-2 border-b border-blue-300/40`}><td colSpan={7} /></tr>

  return (
    <React.Fragment>
      {own}
      {children}
      {gapRow}
    </React.Fragment>
  )
}

export default CollapsibleComposite;
